import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Stack,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  MenuItem,
  Switch,
  Button,
  Alert,
  Snackbar,
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import { useMe } from '../api/hooks.js';
import { api } from '../api/client.js';
import { useT } from '../i18n/useT.js';

interface Plan {
  code: string;
  name: string;
  monthlyPriceUsd: number;
  includedSessions: number;
  dailySessions: number | null;
  monthlySessions: number | null;
  maxProjects: number | null;
  maxIntegrations: number | null;
  coverOps: string;
  integrationsEnabled: boolean;
  apmEnabled: boolean;
  piiMasking: boolean;
  ingestPiiMasking: boolean;
  testerEnabled: boolean;
  /** How many members may hold the tester flag. 0 = unlimited when enabled. */
  testerSeats: number;
  retentionDays: number;
  seats: number;
  active: boolean;
}

// Local editable form: the nullable limits are strings so "" can mean unlimited.
type Nullable = 'dailySessions' | 'monthlySessions' | 'maxProjects' | 'maxIntegrations';
type Form = Omit<Plan, Nullable> & Record<Nullable, string>;
const str = (n: number | null) => (n === null ? '' : String(n));
const toForm = (p: Plan): Form => ({
  ...p,
  dailySessions: str(p.dailySessions),
  monthlySessions: str(p.monthlySessions),
  maxProjects: str(p.maxProjects),
  maxIntegrations: str(p.maxIntegrations),
});
const orNull = (v: string) => (v.trim() === '' ? null : Number(v));

export function AdminPlansPage() {
  const all = useT();
  const t = all.admin.plans;
  const needSuperAdmin = all.admin.needSuperAdmin;
  const { data: me } = useMe();
  const qc = useQueryClient();
  const [forms, setForms] = useState<Record<string, Form>>({});
  const [toast, setToast] = useState<string | null>(null);

  const plans = useQuery({
    enabled: !!me?.isSuperAdmin,
    queryKey: ['admin-plans'],
    queryFn: async () => (await api.get('/v1/admin/plans')).data.data as Plan[],
  });

  useEffect(() => {
    if (plans.data) setForms(Object.fromEntries(plans.data.map((p) => [p.code, toForm(p)])));
  }, [plans.data]);

  const savePlan = useMutation({
    mutationFn: async (f: Form) =>
      api.patch(`/v1/admin/plans/${f.code}`, {
        name: f.name,
        monthlyPriceUsd: Number(f.monthlyPriceUsd),
        includedSessions: Number(f.includedSessions),
        dailySessions: orNull(f.dailySessions),
        monthlySessions: orNull(f.monthlySessions),
        maxProjects: orNull(f.maxProjects),
        maxIntegrations: orNull(f.maxIntegrations),
        coverOps: f.coverOps,
        integrationsEnabled: f.integrationsEnabled,
        apmEnabled: f.apmEnabled,
        piiMasking: f.piiMasking,
        ingestPiiMasking: f.ingestPiiMasking,
        testerEnabled: f.testerEnabled,
        testerSeats: Number(f.testerSeats),
        retentionDays: Number(f.retentionDays),
        seats: Number(f.seats),
        active: f.active,
      }),
    onSuccess: (_d, f) => {
      qc.invalidateQueries({ queryKey: ['admin-plans'] });
      qc.invalidateQueries({ queryKey: ['plans'] });
      setToast(`${f.name} plan saved`);
    },
  });

  const patch = (code: string, k: keyof Form, v: string | number | boolean) =>
    setForms((s) => ({ ...s, [code]: { ...s[code]!, [k]: v } }));

  if (me && !me.isSuperAdmin) {
    return <Alert severity="error">{needSuperAdmin}</Alert>;
  }

  /** A nullable limit: blank is unlimited. */
  const limit = (code: string, k: Nullable, width = 90) => (
    <TextField
      size="small"
      value={forms[code]?.[k] ?? ''}
      placeholder="∞"
      onChange={(e) => patch(code, k, e.target.value)}
      sx={{ width }}
      helperText={t.blankInfinite}
      FormHelperTextProps={{ sx: { m: 0, fontSize: 10 } }}
      inputProps={{ inputMode: 'numeric' }}
    />
  );

  const num = (code: string, k: keyof Form, width = 90) => (
    <TextField
      size="small"
      value={(forms[code]?.[k] as string | number) ?? ''}
      onChange={(e) => patch(code, k, e.target.value)}
      sx={{ width }}
      inputProps={{ inputMode: 'numeric' }}
    />
  );

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <PaymentsIcon color="primary" />
        <Typography variant="h5">{t.title}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t.subtitle}
      </Typography>

      <Paper sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t.columns.plan}</TableCell>
              <TableCell>{t.columns.price}</TableCell>
              <TableCell>{t.columns.sessionsPerMonth}</TableCell>
              <TableCell>{t.columns.sessionsPerDay}</TableCell>
              <TableCell>{t.columns.projects}</TableCell>
              <TableCell>{t.columns.integrationTools}</TableCell>
              <TableCell>{t.columns.coverOps}</TableCell>
              <TableCell>{t.columns.retention}</TableCell>
              <TableCell>{t.columns.seats}</TableCell>
              <TableCell align="center">{t.columns.enabled}</TableCell>
              <TableCell align="center">{t.columns.integrations}</TableCell>
              <TableCell align="center">{t.columns.apm}</TableCell>
              <TableCell align="center">{t.columns.pii}</TableCell>
              <TableCell align="center">{t.columns.ingestPii}</TableCell>
              <TableCell align="center">{t.columns.tester}</TableCell>
              <TableCell align="center">{t.columns.testers}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(plans.data ?? []).map((p) => {
              const f = forms[p.code];
              if (!f) return null;
              return (
                <TableRow key={p.code} hover sx={{ opacity: f.active ? 1 : 0.5 }}>
                  <TableCell>
                    <Stack>
                      <TextField
                        size="small"
                        value={f.name}
                        onChange={(e) => patch(p.code, 'name', e.target.value)}
                        sx={{ width: 140 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        {p.code}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{num(p.code, 'monthlyPriceUsd', 80)}</TableCell>
                  <TableCell>{limit(p.code, 'monthlySessions', 110)}</TableCell>
                  <TableCell>{limit(p.code, 'dailySessions', 100)}</TableCell>
                  <TableCell>{limit(p.code, 'maxProjects', 80)}</TableCell>
                  <TableCell>{limit(p.code, 'maxIntegrations', 80)}</TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={f.coverOps}
                      onChange={(e) => patch(p.code, 'coverOps', e.target.value)}
                      sx={{ width: 120 }}
                    >
                      {(['none', 'addon', 'included'] as const).map((m) => (
                        <MenuItem key={m} value={m}>
                          {t.coverOpsModes[m]}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>{num(p.code, 'retentionDays', 80)}</TableCell>
                  <TableCell>{num(p.code, 'seats', 70)}</TableCell>
                  <TableCell align="center">
                    <Switch
                      color="success"
                      checked={f.active}
                      onChange={(e) => patch(p.code, 'active', e.target.checked)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={f.integrationsEnabled}
                      onChange={(e) => patch(p.code, 'integrationsEnabled', e.target.checked)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={f.apmEnabled}
                      onChange={(e) => patch(p.code, 'apmEnabled', e.target.checked)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={f.piiMasking}
                      onChange={(e) => patch(p.code, 'piiMasking', e.target.checked)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={f.ingestPiiMasking}
                      onChange={(e) => patch(p.code, 'ingestPiiMasking', e.target.checked)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={f.testerEnabled}
                      onChange={(e) => patch(p.code, 'testerEnabled', e.target.checked)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      size="small"
                      value={f.testerSeats}
                      disabled={!f.testerEnabled}
                      onChange={(e) => patch(p.code, 'testerSeats', e.target.value)}
                      sx={{ width: 80 }}
                      helperText={t.zeroInfinite}
                      FormHelperTextProps={{ sx: { m: 0, fontSize: 10 } }}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={savePlan.isPending}
                      onClick={() => savePlan.mutate(f)}
                    >
                      Save
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
        message={toast ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
